import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, Upload, Copy, ExternalLink, ArrowLeft } from "lucide-react";
import { MEXICAN_STATES } from "@/lib/mexican-states";
import { DISCIPLINAS } from "@/lib/disciplinas";
import { PROFILE_IMAGES_BUCKET, getProfileImageUrl } from "@/lib/profile-images";
import { EmergencyCardSection } from "@/components/emergency-card-section";

export const Route = createFileRoute("/_authenticated/perfil")({
  head: () => ({ meta: [{ title: "Mi perfil — Brulo" }] }),
  validateSearch: (search: Record<string, unknown>): { tab?: "emergencia" } =>
    search['tab'] === "emergencia" ? { tab: "emergencia" } : {},

  component: PerfilPage,
});


const BIO_MAX = 160;
const IMG_MAX = 3 * 1024 * 1024;
const ALLOWED = ["image/png", "image/jpeg", "image/webp"];

type ProfileRow = {
  id: string;
  full_name: string | null;
  username: string | null;
  bio: string | null;
  avatar_url: string | null;
  city: string | null;
  estado: string | null;
  disciplinas: string[] | null;
};

function PerfilPage() {
  const { tab: initialTab } = Route.useSearch();
  const [tab, setTab] = useState<"general" | "emergencia">(initialTab ?? "general");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);

  const [fullName, setFullName] = useState("");
  const [bio, setBio] = useState("");
  const [city, setCity] = useState("");
  const [estado, setEstado] = useState("");
  const [disciplinas, setDisciplinas] = useState<string[]>([]);
  const [avatarPath, setAvatarPath] = useState<string | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }
      setUserId(user.id);
      const { data } = await supabase
        .from("profiles")
        .select("id,full_name,username,bio,avatar_url,city,estado,disciplinas")
        .eq("id", user.id)
        .maybeSingle<ProfileRow>();
      if (data) {
        setFullName(data.full_name ?? "");
        setBio(data.bio ?? "");
        setCity(data.city ?? "");
        setEstado(data.estado ?? "");
        setDisciplinas(data.disciplinas ?? []);
        setAvatarPath(data.avatar_url);
        setUsername(data.username);
        setAvatarUrl(await getProfileImageUrl(data.avatar_url));
      }
      setLoading(false);
    })();
  }, []);

  const toggleDisciplina = (key: string) => {
    setDisciplinas((prev) => (prev.includes(key) ? prev.filter((d) => d !== key) : [...prev, key]));
  };

  const onUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !userId) return;
    if (!ALLOWED.includes(f.type)) { toast.error("Usa PNG, JPG o WEBP."); return; }
    if (f.size > IMG_MAX) { toast.error("Máximo 3MB."); return; }
    setUploading(true);
    const ext = f.name.split(".").pop()?.toLowerCase() || "png";
    const path = `${userId}/avatar-${Date.now()}.${ext}`;
    const { error: upErr } = await supabase.storage
      .from(PROFILE_IMAGES_BUCKET)
      .upload(path, f, { cacheControl: "3600", upsert: true, contentType: f.type });
    if (upErr) { setUploading(false); toast.error(upErr.message); return; }
    if (avatarPath && avatarPath !== path) {
      await supabase.storage.from(PROFILE_IMAGES_BUCKET).remove([avatarPath]);
    }
    setAvatarPath(path);
    setAvatarUrl(await getProfileImageUrl(path));
    setUploading(false);
    toast.success("Foto actualizada");
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    if (!fullName.trim()) { toast.error("El nombre es obligatorio."); return; }
    if (bio.length > BIO_MAX) { toast.error(`La bio debe ser de máximo ${BIO_MAX} caracteres.`); return; }
    setSaving(true);
    const { data, error } = await supabase
      .from("profiles")
      .update({
        full_name: fullName.trim(),
        bio: bio.trim() || null,
        city: city.trim() || null,
        estado: estado || null,
        disciplinas: disciplinas.length ? disciplinas : null,
        avatar_url: avatarPath,
      })
      .eq("id", userId)
      .select("username")
      .maybeSingle();
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    if (data?.username) setUsername(data.username);
    toast.success("Perfil actualizado");
  };

  if (loading) {
    return <div className="mx-auto max-w-3xl px-4 py-12 text-muted-foreground">Cargando…</div>;
  }

  const publicUrl = username ? `https://brulo.fit/atleta/${username}` : null;

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-6 py-8 space-y-8">
      <div>
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> Volver
        </Link>
      </div>
      <header>
        <h1 className="font-display text-3xl md:text-4xl">Mi perfil</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {tab === "general"
            ? "Esta información se muestra públicamente en tu página de atleta."
            : "Información médica privada para emergencias durante las rodadas."}
        </p>
      </header>

      <div className="flex gap-2 border-b border-border">
        {([
          ["general", "Información general"],
          ["emergencia", "Ficha de emergencia"],
        ] as const).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={`px-4 py-2.5 text-sm font-semibold -mb-px border-b-2 transition ${
              tab === key
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "emergencia" ? (
        userId ? <EmergencyCardSection userId={userId} /> : null
      ) : (
      <>
      {/* Public URL */}
      {publicUrl && (
        <section className="rounded-lg border border-border bg-card p-5 space-y-3">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">URL pública de tu perfil</p>
          <p className="font-mono text-sm break-all">{publicUrl}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              type="button"
              onClick={async () => {
                await navigator.clipboard.writeText(publicUrl);
                toast.success("Enlace copiado");
              }}
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90"
            >
              <Copy className="w-4 h-4" /> Copiar enlace
            </button>
            <a
              href={`/atleta/${username}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md border-2 border-primary text-sm font-semibold hover:bg-primary/10"
            >
              <ExternalLink className="w-4 h-4" /> Ver mi perfil
            </a>
          </div>
        </section>
      )}

      <form onSubmit={onSave} className="space-y-6">
        {/* Avatar */}
        <section className="rounded-lg border border-border bg-card p-5">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-3">Foto de perfil</p>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border border-border">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl font-display text-muted-foreground">
                  {(fullName || "?").charAt(0).toUpperCase()}
                </div>
              )}
            </div>
            <div>
              <button
                type="button"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-border text-sm hover:bg-muted/50 disabled:opacity-60"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {avatarPath ? "Cambiar foto" : "Subir foto"}
              </button>
              <p className="text-xs text-muted-foreground mt-1.5">PNG, JPG o WEBP. Máximo 3MB.</p>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onUpload}
                className="hidden"
              />
            </div>
          </div>
        </section>

        {/* Name */}
        <div>
          <label className="text-sm font-semibold block mb-1.5">Nombre</label>
          <input
            value={fullName}
            onChange={(e) => setFullName(e.target.value.slice(0, 80))}
            required
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm"
          />
          {username && (
            <p className="text-xs text-muted-foreground mt-1">Tu usuario público: @{username}</p>
          )}
        </div>

        {/* Bio */}
        <div>
          <label className="text-sm font-semibold block mb-1.5">Bio</label>
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
            rows={3}
            placeholder="Cuéntale a la comunidad sobre ti…"
            className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1 text-right">{bio.length}/{BIO_MAX}</p>
        </div>

        {/* Disciplinas */}
        <div>
          <label className="text-sm font-semibold block mb-2">Disciplinas</label>
          <div className="flex flex-wrap gap-2">
            {DISCIPLINAS.map((d) => {
              const active = disciplinas.includes(d.key);
              return (
                <button
                  type="button"
                  key={d.key}
                  onClick={() => toggleDisciplina(d.key)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition ${
                    active
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-card text-foreground border-border hover:border-primary/50"
                  }`}
                >
                  <span>{d.emoji}</span> {d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* City and state */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-semibold block mb-1.5">Ciudad</label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value.slice(0, 80))}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm"
            />
          </div>
          <div>
            <label className="text-sm font-semibold block mb-1.5">Estado</label>
            <select
              value={estado}
              onChange={(e) => setEstado(e.target.value)}
              className="w-full px-3 py-2 bg-input border border-border rounded-md text-sm"
            >
              <option value="">Selecciona…</option>
              {MEXICAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="pt-2">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-primary text-primary-foreground text-sm font-semibold hover:opacity-90 disabled:opacity-60"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Guardar cambios
          </button>
        </div>
      </form>
      </>
      )}
    </div>
  );
}
